import {
  IOrg,
  ISearchPerson,
  SuggestedOrgInviteReason,
  SortDirection,
  SortType,
  SearchPersonStatusResponse,
  ActivityFilter,
} from "@schema";

// libraries
import { makeStyles, Button } from "@material-ui/core";
import React, { useState, useEffect } from "react";
import { createRefetchContainer, RelayRefetchProp } from "react-relay";
import { SearchField } from "@clockwise/web-commons/src/ui/admin-panel/billing-groups-members/search-field";
import { getFullName } from "@clockwise/web-commons/src/ui/admin-panel/billing-groups-members/billing-groups-members.util";
import { logger } from "@clockwise/client-commons/src/util/logger";
import {
  AugmentedPersonMap,
  InvitePerson,
} from "../admin-panel/admin-panel-view/admin-panel-billing-groups-members/AdminPanelBillingGroupsMembers.d";

import { getBillingGroupForId, getUserRoleForBillingGroup } from "@util/billing-group.util";
import { getValue } from "@util/errorable.util";

import { adminSelectQuery, adminSelectFragments } from "./AdminSelect.gql";
import { greys } from "@clockwise/web-commons/src/styles/color.styles";
import { size } from "@clockwise/web-commons/src/styles/type.styles";
import { UserAvatar } from "@clockwise/web-commons/src/ui/UserAvatar";
import { BillingGroupRoleEnum } from "@clockwise/web-commons/src/ui/admin-panel/billing-groups-members/billing-groups-members.type";

export interface AdminSelectProps {
  onReassignNewAdmin: (userId: string, billingGroupId: string) => void;
  org: IOrg;
  relay: RelayRefetchProp;
  billingGroupId: string;
  closeAdminSelectDialog: () => void;
}

const useStyles = makeStyles({
  button__row: {
    display: "flex",
    justifyContent: "flex-end",
  },
  results__table: {
    display: "flex",
    flexDirection: "column",
    overflowY: "scroll",
    maxHeight: "170px",
  },
  cell__content: {
    display: "flex",
    alignItems: "center",
    border: "none",
    "& > div:last-child": {
      marginLeft: 10,
    },
    padding: "10px",
    "&:hover": {
      backgroundColor: greys.paleGrey,
    },
    cursor: "pointer",
    borderRadius: "3px",
  },
  cell__name: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 150,
  },
  cell__row: {
    display: "flex",
    alignItems: "center",
  },
  cell__text: {
    color: greys.dark,
  },

  cell__subtext: {
    fontSize: size.md,
    color: greys.slate,
  },
});

export const AdminSelectCmpt = (props: AdminSelectProps) => {
  const { billingGroupId, org, relay, closeAdminSelectDialog, onReassignNewAdmin } = props;

  const classes = useStyles();
  // ~-~-~-~-~-~-~-
  // State
  // ~-~-~-~-~-~-~-
  const [adminSearchQuery, setAdminSearchQuery] = useState<string>("");
  const [selectedAdmin, setSelectedAdmin] = useState<InvitePerson | null>(null);
  const [searchError, setSearchError] = useState<boolean>(false);
  // ~-~-~-~-~-~-~-
  // Lifecycle
  // ~-~-~-~-~-~-~-

  useEffect(() => {
    if (selectedAdmin) {
      setAdminSearchQuery(selectedAdmin.targetCalendarId);
      setCurrentSuggestedPersons([selectedAdmin]);
    }
  }, [selectedAdmin]);

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-

  const getCurrentPersonsList = (augmentedPersons: AugmentedPersonMap) => {
    if (!Object.keys(augmentedPersons)) {
      return [];
    }
    return Object.keys(augmentedPersons).map((k) => augmentedPersons[k]);
  };

  const getAugmentedPersons = () => {
    let persons: ISearchPerson[] = [];
    const personlist = org.orgPersonListPaginatedErrorable;
    if (personlist.__typename !== "OrgPersonListPaginated") {
      return {} as AugmentedPersonMap;
    }
    persons = personlist.searchPersons;

    const result = persons.reduce((accumulator: AugmentedPersonMap, nextPerson) => {
      const { primaryCalendarId, profile } = nextPerson.person;
      const recentlySent = false;
      const recentlyRequested = false;

      const primaryBillingGroupIdErrorable = getValue(
        nextPerson.person.primaryBillingGroupIdErrorable,
      );

      const maybeBillingGroup = primaryBillingGroupIdErrorable
        ? getBillingGroupForId(org, primaryBillingGroupIdErrorable.primaryBillingGroupId)
        : undefined;

      if (nextPerson.status === SearchPersonStatusResponse.User && !nextPerson.person.isYou) {
        accumulator[primaryCalendarId] = {
          status: nextPerson.status,
          targetCalendarId: primaryCalendarId,
          givenName: profile ? profile.givenName : primaryCalendarId,
          familyName: profile ? profile.familyName : "",
          externalImageUrl: profile ? profile.externalImageUrl : "",
          isSuggested: false,
          recentlySent,
          numberOfSharedMeetings: 0,
          numberOfOrganizedMeetings: 0,
          inviteReason: SuggestedOrgInviteReason.AttendSameMeetings,
          totalScore: 0,
          billingGroup: maybeBillingGroup,
          signUpMillis: nextPerson.person.signUpMillis,
          userId: nextPerson.person.userId,
          personId: nextPerson.person.personId,
          role: maybeBillingGroup
            ? getUserRoleForBillingGroup(maybeBillingGroup, nextPerson.person)
            : BillingGroupRoleEnum.None,
          isYou: nextPerson.person.isYou,
          recentlyRequested,
        };
      }

      return accumulator;
    }, {});
    return result;
  };

  const augmentedPersons = getAugmentedPersons();

  const [currentSuggestedPersons, setCurrentSuggestedPersons] = useState<InvitePerson[]>(
    getCurrentPersonsList(augmentedPersons),
  );

  useEffect(() => {
    const newAugmentedPersons = getAugmentedPersons();
    const newPersons = getCurrentPersonsList(newAugmentedPersons);
    if (newPersons.length === 0) {
      setSearchError(true);
    }
    setCurrentSuggestedPersons(newPersons);
  }, [org]);

  // ~-~-~-~-~-~-~-
  // Callbacks
  // ~-~-~-~-~-~-~-

  const handleAdminSearch = (query: string) => {
    setSearchError(false);
    const newSearchQuery = query || "";
    setAdminSearchQuery(newSearchQuery);

    relay.refetch(
      {
        orgRelayId: org.id,
        searchQuery: newSearchQuery,
        sortType: SortType.FullName,
        sortDirection: SortDirection.Ascending,
        activityFilter: ActivityFilter.CurrentUser,
      },
      undefined,
      (error?: Error) => {
        if (error) {
          logger.error("Error on AdminPanelBillingGroupsMembers search refetch", error);
        }
      },
      {
        force: true,
      },
    );
  };

  const onSelectSuggestedPerson = (person: InvitePerson) => {
    setSelectedAdmin(person);
  };

  const onClickConfirm = () => {
    if (!!selectedAdmin?.userId && !!billingGroupId) {
      onReassignNewAdmin(selectedAdmin.userId, billingGroupId);
    }
  };

  const renderSuggestions = (suggestions: InvitePerson[]) => {
    return suggestions.map((person) => {
      const name = getFullName({
        givenName: person.givenName,
        familyName: person.familyName,
      });
      return (
        <li
          className={classes.cell__content}
          key={person.personId}
          onClick={() => onSelectSuggestedPerson(person)}
        >
          <UserAvatar profile={person} size="medium" />
          <div>
            <div className={classes.cell__row}>
              <div className={classes.cell__text}>{name}</div>
            </div>
            <div className={classes.cell__subtext}>{person.targetCalendarId}</div>
          </div>
        </li>
      );
    });
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-

  return (
    <div>
      <SearchField value={adminSearchQuery} onSearch={handleAdminSearch} />
      <ul className={classes.results__table}>{renderSuggestions(currentSuggestedPersons)}</ul>
      {searchError && <p> No results found </p>}
      <div className={classes.button__row}>
        <Button variant="text" onClick={closeAdminSelectDialog}>
          Close
        </Button>
        <Button variant="text" color="primary" onClick={onClickConfirm}>
          Confirm
        </Button>
      </div>
    </div>
  );
};

export const AdminSelect = createRefetchContainer(
  AdminSelectCmpt,
  adminSelectFragments,
  adminSelectQuery,
);
